import { configKeyTone, explainConfigState } from "../../lib/configuration";
import { timestamp } from "../../lib/format";
import { TONE_TEXT } from "../../lib/status";
import type { ConfigKeyReport } from "../../lib/types";
import { StatusMark } from "../ui/Status";
import { MicroLabel, SectionTitle } from "../ui/Text";

/**
 * Spec 4.4's boot-time push, per key.
 *
 * The station-level drift flag can only say *something* is wrong. This says which
 * key, what was asked, what came back, and what to do about it — because
 * "ignored" and "readonly" send an operator to entirely different places: one is
 * worth retrying, the other needs the storage budget recalculated.
 */

export function ConfigurationReport({
  report,
  checkedAt,
  zone,
}: {
  readonly report: readonly ConfigKeyReport[] | null;
  readonly checkedAt: string | null;
  readonly zone: string;
}) {
  return (
    <section className="flex flex-col gap-4">
      <SectionTitle
        action={
          checkedAt === null ? undefined : (
            <MicroLabel className="tracking-normal normal-case">
              checked {timestamp(checkedAt, zone)}
            </MicroLabel>
          )
        }
      >
        Configuration
      </SectionTitle>

      {report === null || report.length === 0 ? (
        <p className="m-0 text-body text-ink-muted">
          {/* Deliberately not "all good". A station that has not booted since this
              check existed is unknown, and saying otherwise would vouch for it. */}
          This station has not booted since its configuration was last checked. The server pushes
          its metering policy on every BootNotification.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {report.map((entry) => {
            const tone = configKeyTone(entry.state);
            return (
              <li key={entry.key} className="flex flex-col gap-1 border-t border-rule pt-2">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <span className="inline-flex items-center gap-2">
                    <StatusMark tone={tone} />
                    <span className="text-data text-ink">{entry.key}</span>
                  </span>
                  <span className={`text-micro tracking-normal normal-case ${TONE_TEXT[tone]}`}>
                    {entry.state}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-micro tracking-normal normal-case text-ink-muted">
                  {/* Both halves, always. The verdict alone does not survive an
                      operator asking "compared to what?". */}
                  <span>
                    set <span className="text-ink">{entry.desired}</span>
                  </span>
                  <span>
                    reported <span className="text-ink">{entry.reported ?? "nothing"}</span>
                  </span>
                  <span>answered {entry.writeStatus}</span>
                </div>
                {entry.state === "applied" ? null : (
                  <span className="text-micro tracking-normal normal-case text-ink-faint">
                    {explainConfigState(entry.state)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
